import type { Column } from '@tanstack/react-table';

import Icon from '@/components/Icon/Icon';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

const DISPLAY_LABEL_REGEX = /^(.+?)\[.+\]$/;

export interface DataTableColumnHeaderProps<
  TData,
  TValue,
> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  align?: 'left' | 'right';
  /**
   * 最大幅（px）を指定するとテキストが切り詰められ、ホバー時にツールチップが表示される
   * CSVインポート等で使用する場合、セル幅と同じ値を指定することで統一された表示になる
   */
  maxWidth?: number;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  align = 'left',
  className,
  maxWidth,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.dataTable.columnAction',
  });

  const { t: tHeaders } = useTranslation(defaultNS, {
    keyPrefix: 'component.dataTable.headers',
  });

  /**
   * 1. 表示ラベル: API から翻訳済みラベルが返される場合はそれを使用
   * 2. i18nKey: 表示ラベルがない場合は従来の翻訳キーを使用
   * 3. デフォルト値: どちらもない場合は "-" を表示
   */
  const displayText = (() => {
    const meta = column.columnDef.meta;

    if (meta?.displayLabel) {
      return extractDisplayText(meta.displayLabel);
    }

    if (meta?.i18nKey) {
      return tHeaders(meta.i18nKey);
    }

    return '-';
  })();

  const wrapHeader = column.columnDef.meta?.wrapHeader ?? false;

  // maxWidthが指定されている場合はテキストを切り詰めてツールチップを表示
  const renderText = () => {
    if (maxWidth) {
      return (
        <TooltipWrapper content={displayText} delayDuration={100} ellipsisOnly>
          <span
            className="inline-block truncate"
            style={{ maxWidth: `${maxWidth}px` }}
          >
            {displayText}
          </span>
        </TooltipWrapper>
      );
    }
    return (
      <span
        className={cn('inline-block', wrapHeader && 'break-keep text-left')}
      >
        {displayText}
      </span>
    );
  };

  return (
    <DropdownMenu>
      <div
        className={cn(
          'flex items-center',
          align === 'left' && 'justify-start',
          align === 'right' && 'justify-end',
          className,
        )}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-8 font-semibold text-neutral-muted-on-fill data-[state=open]:bg-primary-muted-fill',
              wrapHeader && 'h-auto min-h-8 whitespace-normal',
              align === 'left' && '-ml-3',
              align === 'right' && '-mr-3',
            )}
          >
            <span className="inline-flex items-center gap-1">
              {renderText()}
              {column.getCanSort() && (
                <Icon
                  iconName={
                    column.getIsSorted() === 'desc'
                      ? 'ChevronDownIcon'
                      : column.getIsSorted() === 'asc'
                        ? 'ChevronUpIcon'
                        : 'ChevronsUpDownIcon'
                  }
                  variant="xs"
                  right
                />
              )}
            </span>
          </Button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent align="start">
        {column.getCanSort() && (
          <>
            <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
              <Icon iconName="ChevronUpIcon" text={t('sortAsc')} variant="xs" />
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
              <Icon
                iconName="ChevronDownIcon"
                text={t('sortDesc')}
                variant="xs"
              />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {column.getCanHide() && (
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <Icon iconName="EyeOff" text={t('hide')} variant="xs" />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 表示ラベルから表示テキストを抽出
 *
 * @param displayLabel "従業員番号[employeeNumber]" または undefined
 * @returns "従業員番号" または "-"
 */
function extractDisplayText(displayLabel?: string): string {
  if (!displayLabel || displayLabel.trim() === '') {
    return '-';
  }

  const match = displayLabel.match(DISPLAY_LABEL_REGEX);
  return match && match[1] ? match[1].trim() : displayLabel;
}
